import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import './UsageAnalyticsPage.css';
import {
  fetchUsageMetrics,
  UsageMetrics,
  CategoryLatency,
} from '../../client/UsageAnalyticsClient';
import { loadErrorText, toLoadError, type LoadError } from '../../utils/loadError';
import { getUserInfo } from '../../utils/userInfo';
import { evalAgentLabel } from "../../agents";
import AskEvalAgent from '../../components/AskEvalAgent/AskEvalAgent';

/** The page's agent and date range, named in every ask-the-agent question. */
// A type alias, not an interface: i18next's options need an index signature.
type AskScope = {
  agent: string;
  start: string;
  end: string;
};

// ── Constants ────────────────────────────────────────────────────────────────

// Agent selection lives in EvalLayout's sidebar and arrives via
// <Outlet context={{ agent }} />; this page only needs the label.

interface EvalOutletContext {
  agent: string;
}

/** YYYY-MM-DD `days` ago. */
function daysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}


// ── Helpers ──────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return n.toLocaleString('en-US');
  return String(n);
}

function getBadgeClass(channel: string): string {
  const lower = channel.toLowerCase();
  if (lower.includes('teams')) return 'eval-usage__badge--teams';
  if (lower.includes('slack')) return 'eval-usage__badge--slack';
  if (lower.includes('web')) return 'eval-usage__badge--web';
  if (lower.includes('api')) return 'eval-usage__badge--api';
  return '';
}

function formatMs(ms: number): string {
  if (ms <= 0) return '—';
  const sec = ms / 1000;
  if (sec < 1) return `${Math.round(ms)}ms`;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
}

function formatLastActive(isoStr: string, dateLocale: string): string {
  if (!isoStr) return '-';
  try {
    const date = new Date(isoStr);
    return date.toLocaleString(dateLocale, {
      timeZone: 'Asia/Shanghai',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return isoStr;
  }
}

// ── User Components ──────────────────────────────────────────────────────────

const BADGE_PHOTO_URL = 'https://badgephotos.corp.amazon.com/?uid=';
const PHONETOOL_URL = 'https://phonetool.amazon.com/users/';

function UserAvatar({ login }: { login: string }) {
  const [failed, setFailed] = useState(false);
  if (!login) return <span>—</span>;
  return (
    <a className="user-avatar" href={`${PHONETOOL_URL}${login}`} target="_blank" rel="noopener">
      {!failed ? (
        <img
          className="user-avatar__img"
          src={`${BADGE_PHOTO_URL}${encodeURIComponent(login)}`}
          alt=""
          referrerPolicy="origin"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="user-avatar__fallback">{login.charAt(0).toUpperCase()}</span>
      )}
      {login}
    </a>
  );
}

function UserRole({ login }: { login: string }) {
  const [title, setTitle] = useState<string>('');
  useEffect(() => {
    getUserInfo(login).then(info => {
      if (info) setTitle(info.businessTitle);
    });
  }, [login]);
  return <span className="user-role">{title || '—'}</span>;
}

// ── WoW Comparison Arrow ─────────────────────────────────────────────────────

/** Renders a colored arrow with percentage showing period-over-period change.
 *  For latency: negative change = improvement (green ↓), positive = regression (red ↑).
 */
function WoWArrow({ changePercent }: { changePercent: number }) {
  if (changePercent === 0) return null;
  const improved = changePercent < 0;
  const cls = improved ? 'eval-usage__wow eval-usage__wow--good' : 'eval-usage__wow eval-usage__wow--bad';
  const arrow = improved ? '↓' : '↑';
  const pct = Math.abs(changePercent).toFixed(1);
  return (
    <span className={cls}>
      {arrow}{pct}%
    </span>
  );
}

// ── Latency Info Tooltip ─────────────────────────────────────────────────────

/** Info icon that shows scheduled vs interactive latency breakdown on hover.
 *  `metric` controls which value to display: 'avg' for Avg Response card, 'p95' for P95 card.
 */
function LatencyInfoTooltip({
  scheduled,
  interactive,
  fmtMs,
  metric,
}: {
  scheduled: CategoryLatency | null;
  interactive: CategoryLatency | null;
  fmtMs: (ms: number) => string;
  metric: 'avg' | 'p95';
}) {
  const { t } = useTranslation('eval');
  if (!scheduled && !interactive) return null;
  const getVal = (cat: CategoryLatency) => metric === 'avg' ? cat.avgMs : cat.p95Ms;
  const getWoWPercent = (cat: CategoryLatency) => {
    if (!cat.wow) return null;
    return metric === 'avg' ? cat.wow.avgChangePercent : cat.wow.p95ChangePercent;
  };
  const label = metric === 'avg' ? t('usage.avgResponse') : t('usage.p95Response');
  return (
    <span className="eval-usage__info-icon-wrap">
      <svg className="eval-usage__info-icon" viewBox="0 0 16 16" fill="none" aria-label={t('usage.latencyBreakdown')}>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.2" />
        <path d="M8 7v4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        <circle cx="8" cy="5" r="0.8" fill="currentColor" />
      </svg>
      <div className="eval-usage__tooltip">
        <div className="eval-usage__tooltip-title">{label}</div>
        {scheduled && (
          <div className="eval-usage__tooltip-row">
            <span className="eval-usage__tooltip-label">{t('usage.scheduled')}</span>
            <span>{fmtMs(getVal(scheduled))}</span>
            {getWoWPercent(scheduled) != null && <WoWArrow changePercent={getWoWPercent(scheduled)!} />}
          </div>
        )}
        {interactive && (
          <div className="eval-usage__tooltip-row">
            <span className="eval-usage__tooltip-label">{t('usage.interactive')}</span>
            <span>{fmtMs(getVal(interactive))}</span>
            {getWoWPercent(interactive) != null && <WoWArrow changePercent={getWoWPercent(interactive)!} />}
          </div>
        )}
      </div>
    </span>
  );
}

// ── Usage Tab ────────────────────────────────────────────────────────────────

function UsageTab({
  metrics,
  loading,
  error,
  ask,
}: {
  metrics: UsageMetrics | null;
  loading: boolean;
  error: LoadError | null;
  ask: AskScope;
}) {
  const { t } = useTranslation('eval');
  if (loading) {
    return <div className="eval-usage__loading">{t('usage.loading')}</div>;
  }
  if (error) {
    return <div className="eval-usage__error">{loadErrorText(error, t)}</div>;
  }
  if (!metrics) {
    return <div className="eval-usage__loading">{t('shared.noData')}</div>;
  }

  const maxSessions = Math.max(...metrics.dailySessions.map((d) => d.count), 1);

  const conversations = metrics.messages - metrics.toolCalls;
  const latency = metrics.responseLatency;

  const kpis: { label: string; value: string; sub?: string; wowPercent?: number; hasLatencyBreakdown?: boolean; metric?: 'avg' | 'p95' }[] = [
    { label: t('usage.activeUsers'), value: formatNumber(metrics.activeUsers) },
    { label: t('usage.sessions'), value: formatNumber(metrics.sessions) },
    { label: t('usage.messages'), value: formatNumber(metrics.messages), sub: t('usage.messagesSub', { conversations: formatNumber(conversations), toolCalls: formatNumber(metrics.toolCalls) }) },
    { label: t('usage.inputTokens'), value: formatNumber(metrics.inputTokens) },
    { label: t('usage.outputTokens'), value: formatNumber(metrics.outputTokens) },
    { label: t('usage.toolCalls'), value: formatNumber(metrics.toolCalls) },
    ...(latency && latency.sampleCount > 0 ? [
      { label: t('usage.avgResponse'), value: formatMs(latency.avgMs), wowPercent: latency.wow?.avgChangePercent, hasLatencyBreakdown: true, metric: 'avg' as const },
      { label: t('usage.p95Response'), value: formatMs(latency.p95Ms), wowPercent: latency.wow?.p95ChangePercent, hasLatencyBreakdown: true, metric: 'p95' as const },
    ] : []),
  ];

  const maxChannelCount = Math.max(
    ...metrics.channelDistribution.map((c) => c.count),
    1,
  );

  return (
    <>
      {/* KPI Cards */}
      <div className="eval-usage__kpi-container">
        <h3 className="eval-usage__section-title">
          {t('usage.section')}
          <AskEvalAgent
            question={t('askAgent.usage.overview', {
              ...ask,
              users: formatNumber(metrics.activeUsers),
              sessions: formatNumber(metrics.sessions),
              messages: formatNumber(metrics.messages),
              toolCalls: formatNumber(metrics.toolCalls),
            })}
          />
        </h3>
        <div className="eval-usage__kpi-grid">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="eval-usage__kpi-card">
              <p className="eval-usage__kpi-label">
                {kpi.label}
                {kpi.hasLatencyBreakdown && latency && (
                  <LatencyInfoTooltip
                    scheduled={latency.scheduledLatency}
                    interactive={latency.interactiveLatency}
                    fmtMs={formatMs}
                    metric={kpi.metric!}
                  />
                )}
              </p>
              <p className="eval-usage__kpi-value">
                {kpi.value}
                {kpi.wowPercent != null && <WoWArrow changePercent={kpi.wowPercent} />}
              </p>
              {kpi.sub && <p className="eval-usage__kpi-sub">{kpi.sub}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Daily Sessions Chart */}
      <div className="eval-usage__chart-card">
        <h3 className="eval-usage__section-title">
          {t('usage.dailySessions')}
          <AskEvalAgent question={t('askAgent.usage.daily', ask)} />
        </h3>
        <div className="eval-usage__chart-wrapper">
          <div className="eval-usage__chart-yaxis">
            <span>{formatNumber(maxSessions)}</span>
            <span>{formatNumber(Math.round(maxSessions / 2))}</span>
            <span>0</span>
          </div>
          <div className="eval-usage__chart-container">
            {metrics.dailySessions.map((day) => (
              <div key={day.date} className="eval-usage__chart-bar-wrapper">
                <div
                  className="eval-usage__chart-bar"
                  style={{ height: `${(day.count / maxSessions) * 100}%` }}
                  title={t('usage.dayBar', { date: day.date, n: formatNumber(day.count) })}
                />
                <span className="eval-usage__chart-label">
                  {day.date.slice(5)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Two-Column: Channel Distribution + Response Time (left) | Top Tools (right) */}
      <div className="eval-usage__two-col">
        <div className="eval-usage__channel-card">
          <h3 className="eval-usage__section-title">
            {t('usage.channels')}
            <AskEvalAgent question={t('askAgent.usage.channels', ask)} />
          </h3>
          {metrics.channelDistribution.map((ch) => (
            <div key={ch.channel} className="eval-usage__channel-row">
              <span className="eval-usage__channel-name">{ch.channel}</span>
              <div className="eval-usage__channel-bar-bg">
                <div
                  className="eval-usage__channel-bar-fill"
                  style={{ width: `${(ch.count / maxChannelCount) * 100}%` }}
                />
              </div>
              <span className="eval-usage__channel-count">
                {formatNumber(ch.count)}
              </span>
            </div>
          ))}

          {/* Response Time Distribution — same card */}
          {latency && latency.sampleCount > 0 && (
            <>
              <h3 className="eval-usage__section-title eval-usage__section-title--spaced">
                {t('usage.responseTimes', { n: latency.sampleCount })}
                <AskEvalAgent
                  question={t('askAgent.usage.latency', { ...ask, avg: formatMs(latency.avgMs), p95: formatMs(latency.p95Ms) })}
                />
              </h3>
              <div className="eval-usage__latency-chart">
                {latency.buckets.map((b) => {
                  const pct = latency.sampleCount > 0 ? (b.count / latency.sampleCount) * 100 : 0;
                  return (
                    <div key={b.bucket} className="eval-usage__latency-row">
                      <span className="eval-usage__latency-label">{b.bucket}</span>
                      <div className="eval-usage__latency-bar-bg">
                        <div className="eval-usage__latency-bar" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="eval-usage__latency-count">{b.count}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="eval-usage__table-card">
          <h3 className="eval-usage__section-title">
            {t('usage.topTools')}
            <AskEvalAgent question={t('askAgent.usage.tools', ask)} />
          </h3>
          <table className="eval-usage__table">
            <thead>
              <tr>
                <th>{t('usage.colTool')}</th>
                <th>{t('usage.colCalls')}</th>
              </tr>
            </thead>
            <tbody>
              {metrics.topTools.map((tool) => (
                <tr key={tool.toolName}>
                  <td>
                    {tool.toolName}
                    <AskEvalAgent
                      question={t('askAgent.usage.tool', { ...ask, tool: tool.toolName, count: formatNumber(tool.count) })}
                    />
                  </td>
                  <td className="eval-usage__table-num">
                    {formatNumber(tool.count)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* All Users Table */}
      <AllUsersTable users={metrics.topUsers} ask={ask} />
    </>
  );
}

// ── All Users Table (paginated) ──────────────────────────────────────────────

interface TopUser {
  userId: string;
  sessions: number;
  inputTokens: number;
  outputTokens: number;
  channel: string;
  lastActive: string;
}

const PAGE_SIZE = 20;

type UserSortField = 'userId' | 'sessions' | 'totalTokens' | 'inputTokens' | 'outputTokens' | 'channel' | 'lastActive';

function AllUsersTable({ users, ask }: { users: TopUser[]; ask: AskScope }) {
  const { t } = useTranslation('eval');
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<UserSortField>('totalTokens');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const toggleSort = (field: UserSortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'userId' || field === 'channel' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const filteredUsers = users.filter((u) =>
    u.userId.toLowerCase().includes(search.toLowerCase()),
  );

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let cmp = 0;
    switch (sortField) {
      case 'userId': cmp = a.userId.localeCompare(b.userId); break;
      case 'sessions': cmp = a.sessions - b.sessions; break;
      case 'totalTokens': cmp = (a.inputTokens + a.outputTokens) - (b.inputTokens + b.outputTokens); break;
      case 'inputTokens': cmp = a.inputTokens - b.inputTokens; break;
      case 'outputTokens': cmp = a.outputTokens - b.outputTokens; break;
      case 'channel': cmp = a.channel.localeCompare(b.channel); break;
      case 'lastActive': cmp = a.lastActive.localeCompare(b.lastActive); break;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalPages = Math.ceil(sortedUsers.length / PAGE_SIZE);
  const pagedUsers = sortedUsers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const sortIcon = (field: UserSortField) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div className="eval-usage__full-card">
      <h3 className="eval-usage__section-title">
        {t('usage.allUsers', { n: filteredUsers.length })}
        <AskEvalAgent question={t('askAgent.usage.users', ask)} />
      </h3>
      <div className="eval-usage__search-box">
        <svg className="eval-usage__search-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
          <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          className="eval-usage__search-input"
          placeholder={t('shared.findResources')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          aria-label={t('usage.searchUsers')}
        />
      </div>
      <table className="eval-usage__table">
        <thead>
          <tr>
            <th className="task-sortable" onClick={() => toggleSort('userId')}>{t('usage.colUser')}{sortIcon('userId')}</th>
            <th>{t('usage.colRole')}</th>
            <th className="task-sortable" onClick={() => toggleSort('sessions')}>{t('usage.colSessions')}{sortIcon('sessions')}</th>
            <th className="task-sortable" onClick={() => toggleSort('totalTokens')}>{t('usage.colTotalTokens')}{sortIcon('totalTokens')}</th>
            <th className="task-sortable" onClick={() => toggleSort('inputTokens')}>{t('usage.colInputTokens')}{sortIcon('inputTokens')}</th>
            <th className="task-sortable" onClick={() => toggleSort('outputTokens')}>{t('usage.colOutputTokens')}{sortIcon('outputTokens')}</th>
            <th className="task-sortable" onClick={() => toggleSort('channel')}>{t('usage.colChannel')}{sortIcon('channel')}</th>
            <th className="task-sortable" onClick={() => toggleSort('lastActive')}>{t('usage.colLastActive')}{sortIcon('lastActive')}</th>
          </tr>
        </thead>
        <tbody>
          {pagedUsers.map((user) => (
            <tr key={user.userId}>
              <td>
                <UserAvatar login={user.userId} />
                <AskEvalAgent
                  question={t('askAgent.usage.user', {
                    ...ask,
                    user: user.userId,
                    sessions: formatNumber(user.sessions),
                    tokens: formatNumber(user.inputTokens + user.outputTokens),
                    channel: user.channel || '—',
                  })}
                />
              </td>
              <td><UserRole login={user.userId} /></td>
              <td className="eval-usage__table-num">
                {formatNumber(user.sessions)}
              </td>
              <td className="eval-usage__table-num">
                <strong>{formatNumber(user.inputTokens + user.outputTokens)}</strong>
              </td>
              <td className="eval-usage__table-num">
                {formatNumber(user.inputTokens)}
              </td>
              <td className="eval-usage__table-num">
                {formatNumber(user.outputTokens)}
              </td>
              <td>
                <span
                  className={`eval-usage__badge ${getBadgeClass(user.channel)}`}
                >
                  {user.channel}
                </span>
              </td>
              <td>{formatLastActive(user.lastActive, t('shared.dateLocale'))}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="eval-usage__pagination">
          <button disabled={page === 0} onClick={() => setPage(page - 1)}>{t('shared.prev')}</button>
          <span>{t('shared.page', { page: page + 1, total: totalPages })}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>{t('shared.next')}</button>
        </div>
      )}
    </div>
  );
}

// ── Insights Tab ─────────────────────────────────────────────────────────────

// ── Main Page ────────────────────────────────────────────────────────────────

export default function UsageAnalyticsPage() {
  const { t } = useTranslation('eval');
  const { agent } = useOutletContext<EvalOutletContext>();
  // Concrete date range (default: last 7 days), mirroring the Reports page.
  const [startDate, setStartDate] = useState<string>(() => daysAgo(7));
  const [endDate, setEndDate] = useState<string>(() => todayIso());

  const [metrics, setMetrics] = useState<UsageMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState<LoadError | null>(null);

  const agentLabel = evalAgentLabel(agent);

  const doFetch = useCallback(async (fetchAgent: string, fetchStart: string, fetchEnd: string) => {
    setMetricsLoading(true);
    setMetricsError(null);
    try {
      const data = await fetchUsageMetrics(fetchAgent, 'custom', fetchStart, fetchEnd);
      setMetrics(data);
    } catch (err: unknown) {
      setMetricsError(toLoadError(err, 'usage.loadFailed'));
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  // Fetch whenever the agent or the picked date range changes.
  useEffect(() => {
    if (!startDate || !endDate || endDate < startDate) return;
    doFetch(agent, startDate, endDate);
  }, [agent, startDate, endDate, doFetch]);

  return (
    <div className="eval-usage">
      {/* Header — matches the Reports page: title + last-update + date range */}
      <div className="eval-usage__header">
        <div className="eval-usage__header-left">
          <h1 className="eval-usage__page-title">{t('usage.title', { agent: agentLabel })}</h1>
          <p className="eval-usage__page-subtitle">{t('shared.lastUpdate', { time: new Date().toLocaleString(t('shared.updatedLocale') || undefined) })}</p>
        </div>
        <div className="eval-usage__date-picker">
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label={t('shared.startDate')}
          />
          <span>—</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={todayIso()}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label={t('shared.endDate')}
          />
        </div>
      </div>

      <UsageTab
        metrics={metrics}
        loading={metricsLoading}
        error={metricsError}
        ask={{ agent: agentLabel, start: startDate, end: endDate }}
      />
    </div>
  );
}
